import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { AddFundsModalBody } from './add-funds-modal-body';
import styles from './styles.module.less';
import ModalPortal from '../../../../../components/modal-portal';
import { PhoneVerificationContextProvider } from '../../../../../components/phone-verification/context';
import { emptyObject } from 'ui-components/default-props/empty';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { AddFundsContextProvider, useAddFundsContext } from '../../context/add-funds-context-provider';
import messages from '../funding/messages';
import commonStyles from '../funding/style.module.less';
import SubmitButton from '../funding/submit-button';

/**
 * @param {object} props
 * @param {boolean} props.allowDefaultPurchase
 * @param {object} props.balance
 * @param {string} props.sellerCurrencyCode
 * @param {boolean} props.saveIsDisabled
 * @param {string} props.trackingId
 * @param {(disabled: boolean) => void} props.setSaveIsDisabled
 * @returns {JSX.Element}
 */
const AddFundsModalContent = ({ allowDefaultPurchase, balance, sellerCurrencyCode, saveIsDisabled, trackingId, setSaveIsDisabled }) => {
  const { isSurchargeLoading, previewError } = useAddFundsContext();
  const isDisabled = isSurchargeLoading || previewError || saveIsDisabled;

  return (
    <Modal className={styles.modal}>
      <ModalHeader className={styles.background}>
        <FormattedMessage {...messages.addFunds} />
      </ModalHeader>
      <ModalBody>
        <PhoneVerificationContextProvider>
          <AddFundsModalBody
            allowDefaultPurchase={allowDefaultPurchase}
            balance={balance?.value}
            currencyCode={sellerCurrencyCode}
            setSaveIsDisabled={setSaveIsDisabled}
            trackingId={trackingId}
          />
        </PhoneVerificationContextProvider>
      </ModalBody>
      <ModalFooter className={styles.background}>
        <ModalFooterCancelButton className={commonStyles['button-border']} />
        <SubmitButton disabled={isDisabled} trackingId={trackingId}>
          <FormattedMessage {...messages.addFunds} />
        </SubmitButton>
      </ModalFooter>
    </Modal>
  );
};

/**
 * @typedef {import('../../../../../prop-types/currency-types').Currency} Currency
 * @typedef {{threshold: Currency | null, amount: Currency | null, cutoff: number | null}} AutoFundConfig
 */

/**
 * @param {object} props
 * @param {string} props.sellerCurrencyCode
 * @param {() => void} props.onClose
 * @param {(result: Currency | AutoFundConfig) => void} props.onSuccess
 * @param {object} [props.balance]
 * @param {{amount?: Currency, code?: string}} [props.defaultPurchaseConfig]
 * @param {(amount: Currency) => void | Promise<void>} props.onAddFunds
 * @param {(amount: Currency) => void | Promise<void>} props.onSaveDefaultFundingAmount
 * @param {boolean} [props.allowDefaultPurchase]
 * @param {string} props.trackingId
 * @returns {JSX.Element}
 */
export const AddFundsModal = ({
  onClose,
  sellerCurrencyCode,
  onSuccess,
  balance = emptyObject,
  defaultPurchaseConfig = emptyObject,
  onAddFunds,
  onSaveDefaultFundingAmount,
  allowDefaultPurchase = false,
  trackingId,
}) => {
  const [submitting, setSubmitting] = useAsyncSafeState(false);
  const [saveIsDisabled, setSaveIsDisabled] = useAsyncSafeState(false);
  const trackAddFunds = useTrackWorkflow(Workflows.ADD_FUNDS_FLOW);

  const closeAndTrackAddFundsCancel = () => {
    trackAddFunds(WorkflowSteps.CANCEL);
    onClose();
  };

  return (
    <ModalPortal onCancel={closeAndTrackAddFundsCancel} onClose={onClose}>
      <AddFundsContextProvider
        currencyCode={sellerCurrencyCode}
        defaultPurchaseConfig={defaultPurchaseConfig}
        onAddFunds={onAddFunds}
        onSaveDefaultFundingAmount={onSaveDefaultFundingAmount}
        onSuccess={onSuccess}
        setSubmitting={setSubmitting}
        submitting={submitting}
        trackingId={trackingId}
      >
        <AddFundsModalContent
          allowDefaultPurchase={allowDefaultPurchase}
          balance={balance}
          saveIsDisabled={saveIsDisabled}
          sellerCurrencyCode={sellerCurrencyCode}
          setSaveIsDisabled={setSaveIsDisabled}
          trackingId={trackingId}
        />
      </AddFundsContextProvider>
    </ModalPortal>
  );
};
